import { createAsyncThunk } from '@reduxjs/toolkit';
import { clearHomeRowCache } from '../../components/DJPal/mixpediaHomeData';
import { errMsg } from '../utils/errMsg';
import { requestRefresh } from '../utils/refreshGate';
import { track } from '../../utils/analytics';
import {
    setAuthenticated,
    setUser,
    setAccessToken,
    setRefreshToken,
    setIsLoading,
    setError,
    setShowLoginModal,
    setShowRegisterModal,
    clearAuth,
    setLoginChallenge,
} from '../slices/authSlice';
import { clearFeed } from '../slices/feedSlice';
import { clearLikes } from '../slices/likesSlice';
import { clearFollowedArtists } from '../slices/followedArtistsSlice';
import { clearFollowedLabels } from '../slices/followedLabelsSlice';
import { clearSearchHistoryState } from '../slices/searchHistorySlice';
import { clearToken, clearResults as clearMixpediaResults } from '../slices/djpalSlice';
import { resetNavigation, setActiveCategory, resetFilePath } from '../slices/appSlice';
import { resetFilePath as resetMetadataFiles } from '../slices/fixMetadataSlice';
import { resetOrganizerFilePath } from '../slices/organizerSlice';
import { resetLoudnessFilePath } from '../slices/loudnessSlice';
import { resetCueFiles } from '../slices/cuePointsSlice';
import { resetLogsState } from '../slices/logsSlice';
import { resetInfoForAccountSwitch } from '../slices/infoSlice';
import { clearTier } from '../slices/tierSlice';
import { clearPlaylists } from '../slices/playlistSlice';
import { resetFlow as resetPlannerFlow, clearPendingStart as clearPlannerPendingStart } from '../slices/whatsNextSlice';
import { clearLikedMixes } from '../slices/likedMixesSlice';
import { clearNotifications } from '../slices/notificationsSlice';
import { clearResults as clearSmartRecsResults, setSelectedPlaylist as setSmartRecsPlaylist } from '../slices/smartRecsSlice';
import { resetSet as resetAiSet } from '../slices/aiSetSlice';
import { resetAutomix } from '../slices/automixSlice';
import { resetAudioList } from '../slices/musicPlayerSlice';
import { clearListContext } from '../slices/listContextSlice';
import { updateSettings } from '../slices/settingsSlice';
import { resetRekordboxState } from '../slices/rekordboxSlice';
import { clearDjLibraryChanges } from '../slices/djLibraryChangesSlice';
import { resetSeratoState } from '../slices/seratoSlice';
import { resetTraktorState } from '../slices/traktorSlice';
import { resetVirtualDJState } from '../slices/virtualdjSlice';
import { clearPlannerSetsCache } from '../../utils/plannerSetsCache';
import { clearUserScopedCaches } from '../../utils/userScopedCaches';
import { resetAnalyticsIdentity } from '../../utils/analytics';

// On web, auth tokens live in HttpOnly cookies — never in localStorage and
// never in Redux state. credentials:'include' on every shim fetch carries
// them automatically. On Electron, tokens stay in localStorage + Redux as
// before (lower XSS risk, and existing in-flight desktop builds expect it).
const IS_WEB = typeof window !== 'undefined' && window.electron && !window.electron.db;

export const checkAuthStatus = createAsyncThunk(
    'auth/checkStatus',
    async (_, { dispatch, getState }) => {
        // Transient failures keep tokens + cached user, but only if a user is
        // actually cached — otherwise we fall back to the login modal rather
        // than rendering a half-state (no avatar / wrong tier / etc.).
        const preserveAuthState = (accessToken, refreshToken) => {
            const cachedUser = getState().auth.user;
            if (cachedUser) {
                if (!IS_WEB) {
                    dispatch(setAccessToken(accessToken));
                    dispatch(setRefreshToken(refreshToken));
                }
                dispatch(setAuthenticated(true));
            } else {
                dispatch(setShowLoginModal(true));
            }
        };

        try {
            dispatch(setIsLoading(true));

            // ── Web: bootstrap tokens into Redux from /refresh (cookies carry
            // the refresh token). After bootstrap, the rest of the renderer
            // sees the same Redux shape as Electron and existing call-site
            // gating on accessToken keeps working.
            if (IS_WEB) {
                const cachedUser = getState().auth.user; // from mg_user cookie hint at hydrate
                // Local dev shortcut: cookies don't survive cross-origin, so
                // the `mg_user` hint isn't there even after a fresh login.
                // The shim cached the access token in localStorage on the
                // previous login response — use it directly with /me to
                // restore the session (skips /refresh entirely, which the
                // local proxy can't carry the cookie for).
                const IS_LOCAL_DEV = typeof window !== 'undefined'
                    && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
                if (IS_LOCAL_DEV) {
                    // Trust the cached session blob written by the shim at
                    // login. Skipping the /me validation here avoids the
                    // log-out-on-refresh loop caused by the deployed auth
                    // service's access tokens being short-lived and rotated
                    // via a cookie that doesn't survive cross-origin to
                    // localhost. Individual API calls will still get a 401
                    // if the token actually expired, but the UI shell stays
                    // mounted instead of bouncing the user out of every
                    // refresh.
                    const cachedAccessToken = localStorage.getItem('web-shim:dev-access-token');
                    const cachedUserJson = localStorage.getItem('web-shim:dev-user');
                    let cachedUserObj = null;
                    try { cachedUserObj = cachedUserJson ? JSON.parse(cachedUserJson) : null; } catch (_) { /* corrupt */ }
                    if (cachedAccessToken && cachedUserObj) {
                        dispatch(setAccessToken(cachedAccessToken));
                        const cachedRefreshToken = localStorage.getItem('web-shim:dev-refresh-token');
                        if (cachedRefreshToken) dispatch(setRefreshToken(cachedRefreshToken));
                        dispatch(setUser(cachedUserObj));
                        dispatch(setAuthenticated(true));
                        // Dismiss the login modal if it was shown by a prior
                        // checkAuthStatus pass (capacitor cold-start race:
                        // first pass sees empty localStorage → shows modal,
                        // hydratePrefs then mirrors Keychain → fires
                        // `capacitor-auth-rehydrated` → second pass restores
                        // session here). Without this dispatch, the user
                        // sees the login screen on top of an authenticated
                        // app and thinks they were logged out.
                        dispatch(setShowLoginModal(false));
                        dispatch(setIsLoading(false));
                        return;
                    }
                    // No cached login — wipe partial state and prompt.
                    localStorage.removeItem('web-shim:dev-access-token');
                    localStorage.removeItem('web-shim:dev-refresh-token');
                    localStorage.removeItem('web-shim:dev-user');
                    dispatch(setShowLoginModal(true));
                    dispatch(setIsLoading(false));
                    return;
                }
                if (!cachedUser) {
                    dispatch(setShowLoginModal(true));
                    dispatch(setIsLoading(false));
                    return;
                }
                const refreshResponse = await window.electron.auth.refreshToken();
                if (!refreshResponse || refreshResponse.error || !refreshResponse.accessToken) {
                    const status = refreshResponse?.status;
                    if (status === 401 || status === 403) {
                        dispatch(clearAuth());
                        dispatch(setShowLoginModal(true));
                    } else {
                        console.warn('Web bootstrap refresh failed (transient):', refreshResponse?.error);
                        dispatch(setAuthenticated(true));
                    }
                    dispatch(setIsLoading(false));
                    return;
                }
                dispatch(setAccessToken(refreshResponse.accessToken));
                if (refreshResponse.refreshToken) {
                    dispatch(setRefreshToken(refreshResponse.refreshToken));
                }
                const profileResponse = await window.electron.auth.getProfile(refreshResponse.accessToken);
                if (profileResponse && !profileResponse.error && profileResponse.email) {
                    dispatch(setUser(profileResponse));
                }
                dispatch(setAuthenticated(true));
                dispatch(setIsLoading(false));
                return;
            }

            // ── Electron: tokens in localStorage as before.
            const storedAccessToken = localStorage.getItem('auth:accessToken');
            const storedRefreshToken = localStorage.getItem('auth:refreshToken');

            // Main refreshes the access token itself when a background cloud
            // sync hits a 401 (see refreshMainAccessToken); adopt it so the
            // renderer doesn't immediately refresh a second time.
            if (!window.__mgTokenRefreshedBound && window.electron?.auth?.onTokenRefreshed) {
                window.__mgTokenRefreshedBound = true;
                window.electron.auth.onTokenRefreshed(({ accessToken, refreshToken } = {}) => {
                    if (!accessToken) return;
                    localStorage.setItem('auth:accessToken', accessToken);
                    dispatch(setAccessToken(accessToken));
                    // Refresh tokens rotate: main just adopted a new one, so the
                    // copy the renderer restores from at next launch must follow.
                    if (refreshToken) {
                        localStorage.setItem('auth:refreshToken', refreshToken);
                        dispatch(setRefreshToken(refreshToken));
                    }
                });
            }

            if (!storedAccessToken || !storedRefreshToken) {
                dispatch(setShowLoginModal(true));
                dispatch(setIsLoading(false));
                return;
            }

            const profileResponse = await window.electron.auth.getProfile(storedAccessToken, storedRefreshToken);

            if (profileResponse && !profileResponse.error && profileResponse.email) {
                dispatch(setUser(profileResponse));
                dispatch(setAccessToken(storedAccessToken));
                dispatch(setRefreshToken(storedRefreshToken));
                dispatch(setAuthenticated(true));
                dispatch(setIsLoading(false));
                return;
            }

            // Access token may be expired or invalid, try refresh
            const refreshResponse = await window.electron.auth.refreshToken(storedRefreshToken);

            if (!refreshResponse || refreshResponse.error || !refreshResponse.accessToken) {
                // Only clear tokens if server explicitly rejected the refresh token (401/403)
                // On network errors or timeouts, keep tokens and stay "logged in" with stale data
                const status = refreshResponse?.status;
                if (status === 401 || status === 403) {
                    localStorage.removeItem('auth:accessToken');
                    localStorage.removeItem('auth:refreshToken');
                    dispatch(clearAuth());
                    dispatch(setShowLoginModal(true));
                } else {
                    console.warn('Token refresh failed (transient), keeping auth state:', refreshResponse?.error);
                    preserveAuthState(storedAccessToken, storedRefreshToken);
                }
                dispatch(setIsLoading(false));
                return;
            }

            const newAccessToken = refreshResponse.accessToken;
            localStorage.setItem('auth:accessToken', newAccessToken);

            const newProfileResponse = await window.electron.auth.getProfile(newAccessToken);

            if (newProfileResponse && !newProfileResponse.error && newProfileResponse.email) {
                dispatch(setUser(newProfileResponse));
                dispatch(setAccessToken(newAccessToken));
                dispatch(setRefreshToken(storedRefreshToken));
                dispatch(setAuthenticated(true));
            } else {
                console.warn('Profile fetch failed after refresh, keeping auth state');
                preserveAuthState(newAccessToken, storedRefreshToken);
            }
            dispatch(setIsLoading(false));
        } catch (error) {
            console.error('Error checking auth status:', error);
            if (IS_WEB) {
                const cachedUser = getState().auth.user;
                if (cachedUser) {
                    dispatch(setAuthenticated(true));
                } else {
                    dispatch(setShowLoginModal(true));
                }
            } else {
                const storedToken = localStorage.getItem('auth:accessToken');
                const storedRefresh = localStorage.getItem('auth:refreshToken');
                if (storedToken && storedRefresh) {
                    preserveAuthState(storedToken, storedRefresh);
                } else {
                    dispatch(setShowLoginModal(true));
                }
            }
            dispatch(setIsLoading(false));
        }
    }
);

export const loginUser = createAsyncThunk(
    'auth/login',
    async ({ email, password }, { dispatch }) => {
        try {
            dispatch(setError(null));
            dispatch(setIsLoading(true));

            // Yield to let the spinner paint before the IPC call
            await new Promise(resolve => requestAnimationFrame(resolve));

            console.time('auth:login-api');
            const response = await window.electron.auth.login(email, password);
            console.timeEnd('auth:login-api');

            if (response.error) {
                dispatch(setError(errMsg(response.error)));
                dispatch(setIsLoading(false));
                return;
            }

            // Right password, unseen device: the server emailed a code and
            // issued nothing yet. Hand the modal over to the code step.
            if (response.challenge === 'email_code' && response.challengeId) {
                dispatch(setLoginChallenge({
                    challengeId: response.challengeId,
                    email: response.email || null,
                    expiresIn: response.expiresIn || 600,
                    sentAt: Date.now(),
                    loginEmail: email,
                }));
                dispatch(setIsLoading(false));
                track('login_code_prompt_shown', {});
                return;
            }

            console.time('auth:login-post');
            adoptSignIn(dispatch, response);
            console.timeEnd('auth:login-post');
            // Don't set isLoading(false) here — App.js will clear it after render
        } catch (error) {
            console.error('Login error:', error);
            dispatch(setError('An error occurred. Please try again.'));
            dispatch(setIsLoading(false));
        }
    }
);

// Tokens + user from a completed sign-in (password, code step, or social).
function adoptSignIn(dispatch, response) {
            guardAccountSwitch(dispatch, response.user);
            // Web: tokens go into Redux memory only — no localStorage. The
            // refresh-token cookie is HttpOnly so an XSS can read what Redux
            // holds at most for one rotation cycle (legitimate /refresh
            // invalidates it). Electron: persist to localStorage as before.
            if (!IS_WEB) {
                localStorage.setItem('auth:accessToken', response.accessToken);
                localStorage.setItem('auth:refreshToken', response.refreshToken);
            }
            dispatch(setAccessToken(response.accessToken));
            dispatch(setRefreshToken(response.refreshToken));
            dispatch(setUser(response.user));
            dispatch(setAuthenticated(true));
            dispatch(setLoginChallenge(null));
            dispatch(setShowLoginModal(false));
}

// Second step of a sign-in from an unseen device: the emailed code.
export const verifyLoginCode = createAsyncThunk(
    'auth/verifyLoginCode',
    async ({ code }, { dispatch, getState }) => {
        const challenge = getState().auth.loginChallenge;
        if (!challenge?.challengeId) return;
        const digits = String(code || '').replace(/\D/g, '');
        if (digits.length !== 6) {
            dispatch(setError('Enter the 6-digit code from the email.'));
            return;
        }
        try {
            dispatch(setError(null));
            dispatch(setIsLoading(true));
            const response = await window.electron.auth.loginVerify(challenge.challengeId, digits);
            if (response?.error) {
                dispatch(setError(errMsg(response.error)));
                dispatch(setIsLoading(false));
                track('login_code_failed', { code: response.code || 'unknown' });
                // A dead challenge (expired, locked, consumed) sends the user
                // back to the password step; a plain wrong code stays put.
                if (response.code && response.code !== 'challenge_wrong') dispatch(setLoginChallenge(null));
                return;
            }
            track('login_code_verified', {});
            adoptSignIn(dispatch, response);
        } catch (error) {
            console.error('Login code error:', error);
            dispatch(setError('An error occurred. Please try again.'));
            dispatch(setIsLoading(false));
        }
    }
);

export const resendLoginCode = createAsyncThunk(
    'auth/resendLoginCode',
    async (_, { dispatch, getState }) => {
        const challenge = getState().auth.loginChallenge;
        if (!challenge?.challengeId) return;
        try {
            dispatch(setError(null));
            const response = await window.electron.auth.loginResend(challenge.challengeId);
            if (response?.error) {
                dispatch(setError(errMsg(response.error)));
                if (response.code && response.code !== 'challenge_resend_wait') dispatch(setLoginChallenge(null));
                return;
            }
            dispatch(setLoginChallenge({ ...challenge, expiresIn: response.expiresIn || challenge.expiresIn, sentAt: Date.now() }));
            track('login_code_resent', {});
        } catch (error) {
            dispatch(setError('An error occurred. Please try again.'));
        }
    }
);

export const cancelLoginChallenge = createAsyncThunk(
    'auth/cancelLoginChallenge',
    async (_, { dispatch }) => {
        dispatch(setError(null));
        dispatch(setLoginChallenge(null));
    }
);

export const registerUser = createAsyncThunk(
    'auth/register',
    async (userData, { dispatch }) => {
        try {
            dispatch(setError(null));
            dispatch(setIsLoading(true));

            const response = await window.electron.auth.register({
                first_name: userData.firstName,
                last_name: userData.lastName,
                email: userData.email,
                password: userData.password,
                country: userData.country,
                birth_date: userData.birthDate,
            });

            if (response.error) {
                dispatch(setError(errMsg(response.error)));
                dispatch(setIsLoading(false));
                return { success: false };
            }

            dispatch(setIsLoading(false));
            return { success: true, message: response.message };
        } catch (error) {
            console.error('Registration error:', error);
            dispatch(setError('An error occurred. Please try again.'));
            dispatch(setIsLoading(false));
            return { success: false };
        }
    }
);

// Every clear of USER-SCOPED state (Redux + localStorage caches), shared by
// the two paths that end an account's session on this device: explicit
// logout, and a different account signing in (guardAccountSwitch) — the
// latter covers flows that skip logoutUser, like the 401 forced logout in
// refreshAccessToken.
function wipeUserScopedState(dispatch) {
    // Navigation first: whoever signs in next — the same account or a
    // different one — starts from home, never from the page the previous
    // session left open. resetNavigation empties the tab and the stack; the
    // setActiveCategory that follows re-seeds the hub entry exactly the way
    // App.js does at boot (desktop pushes through the nav middleware, native
    // and real web return early there, as at boot). Going first also means
    // the Mixpedia clear below lands while the tab is no longer active, so
    // it doesn't push a "home" stop onto the fresh stack.
    dispatch(resetNavigation());
    dispatch(setActiveCategory('explore'));
    if (IS_WEB) {
        // Web and native keep the open page in the URL, and App.js re-applies
        // that URL as a deep link on the next sign-in — so it must read as
        // home as well, not just the Redux tab.
        try { window.history.replaceState({ tab: 'feed' }, '', '/'); } catch { /* sandboxed history */ }
    }
    dispatch(clearTier());
    dispatch(clearFeed());
    dispatch(clearLikes());
    dispatch(clearFollowedArtists());
    dispatch(clearFollowedLabels());
    dispatch(clearSearchHistoryState());
    clearHomeRowCache();
    dispatch(clearToken());
    // The Mixpedia page that was open (artist / track / mix / results) is the
    // previous account's position too — back to the home rows.
    dispatch(clearMixpediaResults());
    dispatch(clearPlaylists());
    // Local library: the loaded track lists are the PREVIOUS account's rows.
    // TrackRepository scopes every read to ownerContext (migration 014), so
    // the next account's queries are already correct — but nothing emptied
    // these arrays, so the old rows stayed on screen under the new account's
    // name and looked like that account's library. Found live: two different
    // accounts, one of them owning zero tracks, both shown the same list.
    // One per tab, because each keeps its own copy of the selection.
    //
    // info FIRST and non-negotiable: it is the Library tab's table, and that
    // tab only refetches from the DB when info.enrichedFiles is EMPTY — so
    // with the old rows left here it never asked again, and account B was
    // shown account A's library for as long as the app stayed open.
    dispatch(resetInfoForAccountSwitch());
    dispatch(resetFilePath());
    dispatch(resetMetadataFiles());
    dispatch(resetOrganizerFilePath());
    dispatch(resetLoudnessFilePath());
    dispatch(resetCueFiles());
    // The log rows name the previous account's files, and the counters tally
    // their run.
    dispatch(resetLogsState());
    // Set Planner: wipe the in-progress canvas AND the localStorage saved-sets
    // mirror — it's keyed globally, so without this the next account briefly
    // sees the previous user's sets while the planner mounts.
    dispatch(resetPlannerFlow());
    dispatch(clearPlannerPendingStart());
    clearPlannerSetsCache();
    dispatch(clearLikedMixes());
    dispatch(clearNotifications());
    dispatch(clearSmartRecsResults());
    dispatch(setSmartRecsPlaylist(null));
    dispatch(resetAiSet());
    // Automix names the account's playlist and files. A build still running
    // belongs to the account that left: stop the engine, then clear.
    try { window.electron?.automix?.cancel?.(); } catch (e) { /* web / no bridge */ }
    dispatch(resetAutomix());
    // Player: emptying the queue stops playback everywhere — the native
    // setQueue effect calls engine.stop() on an empty list, and the web
    // player clears via clearPriorAudioLists. The stream is tied to the
    // account's entitlements, so it must not outlive the session.
    dispatch(resetAudioList());
    dispatch(clearListContext());
    dispatch(updateSettings({ djName: '' }));
    // DJ sidebars (desktop): the playlist lists and sync status belong to the
    // account. These resets leave the device-scoped fields (install path,
    // sidebar width) alone — same split as USER_SCOPED_KEYS below.
    dispatch(resetRekordboxState());
    dispatch(resetSeratoState());
    dispatch(resetTraktorState());
    dispatch(resetVirtualDJState());
    // DJ-library change dots (rail + toast card + sidebar banner) mirror the
    // account's sync relationships; DjLibraryChangeListener refetches the
    // owner-filtered pending set for the next account.
    dispatch(clearDjLibraryChanges());
    clearUserScopedCaches();
}

// Device-scoped bookkeeping (deliberately NOT in the wipe): which account the
// user-scoped state on this device currently belongs to.
const LAST_ACCOUNT_KEY = 'mixgoat:lastAccountId';

// Call on every successful credential sign-in, BEFORE the new session's
// tokens/user land. A DIFFERENT account signing in must never inherit the
// previous account's state, even when logoutUser never ran (forced logout,
// crash mid-logout).
function guardAccountSwitch(dispatch, user) {
    const id = user?.id != null ? String(user.id) : null;
    if (!id) return;
    let prev = null;
    try { prev = localStorage.getItem(LAST_ACCOUNT_KEY); } catch { /* private mode */ }
    if (prev && prev !== id) wipeUserScopedState(dispatch);
    try { localStorage.setItem(LAST_ACCOUNT_KEY, id); } catch { /* private mode */ }
}

export const logoutUser = createAsyncThunk(
    'auth/logout',
    async (_, { dispatch, getState }) => {
        // Optimistic logout: clear local state FIRST so the UI flips to the
        // login screen immediately. The server-side revocation is best-effort
        // — fire-and-forget on a microtask so the React commit that hides
        // the authenticated UI happens before the IPC waits.
        //
        // Previous behavior awaited window.electron.auth.logout() before
        // clearing local state, which made the click feel frozen for the
        // full HTTP round-trip to auth.mixgoat.com (1-3s on a slow link).
        // A perf trace showed ~1.9s of "V8 idle" between click and unmount,
        // which is exactly that IPC wait, perceived as a freeze.
        const { auth } = getState();
        const refreshToken = auth.refreshToken;

        if (!IS_WEB) {
            try { localStorage.removeItem('auth:accessToken'); } catch {}
            try { localStorage.removeItem('auth:refreshToken'); } catch {}
        }
        dispatch(clearAuth());
        wipeUserScopedState(dispatch);
        dispatch(setShowLoginModal(true));
        resetAnalyticsIdentity();

        // Best-effort server revocation. Errors are swallowed — the local
        // tokens are already gone, so re-auth is forced on next launch
        // regardless.
        if (IS_WEB || refreshToken) {
            try {
                await window.electron.auth.logout(IS_WEB ? undefined : refreshToken);
            } catch (error) {
                console.warn('[logout] server revocation failed (non-fatal):', error?.message);
            }
        }
    }
);

export const requestPasswordReset = createAsyncThunk(
    'auth/requestPasswordReset',
    async (email, { dispatch }) => {
        try {
            dispatch(setError(null));
            dispatch(setIsLoading(true));

            const response = await window.electron.auth.forgotPassword(email);

            dispatch(setIsLoading(false));

            if (response.error) {
                dispatch(setError(errMsg(response.error)));
                return { success: false };
            }

            return { success: true, message: response.message };
        } catch (error) {
            console.error('Forgot password error:', error);
            dispatch(setError('An error occurred. Please try again.'));
            dispatch(setIsLoading(false));
            return { success: false };
        }
    }
);

export const resetPassword = createAsyncThunk(
    'auth/resetPassword',
    async ({ email, code, newPassword }, { dispatch }) => {
        try {
            dispatch(setError(null));
            dispatch(setIsLoading(true));

            const response = await window.electron.auth.resetPassword({ email, code, newPassword });

            dispatch(setIsLoading(false));

            if (response.error) {
                dispatch(setError(errMsg(response.error)));
                return { success: false };
            }

            return { success: true, message: response.message };
        } catch (error) {
            console.error('Reset password error:', error);
            dispatch(setError('An error occurred. Please try again.'));
            dispatch(setIsLoading(false));
            return { success: false };
        }
    }
);

export const googleLogin = createAsyncThunk(
    'auth/googleLogin',
    async (_, { dispatch }) => {
        try {
            dispatch(setError(null));
            dispatch(setIsLoading(true));

            await new Promise(resolve => requestAnimationFrame(resolve));

            const response = await window.electron.auth.googleLogin();

            if (response.error) {
                const msg = errMsg(response.error);
                if (msg !== 'cancelled') {
                    dispatch(setError(msg));
                }
                dispatch(setIsLoading(false));
                return;
            }

            guardAccountSwitch(dispatch, response.user);
            if (!IS_WEB) {
                localStorage.setItem('auth:accessToken', response.accessToken);
                localStorage.setItem('auth:refreshToken', response.refreshToken);
            }
            dispatch(setAccessToken(response.accessToken));
            dispatch(setRefreshToken(response.refreshToken));
            dispatch(setUser(response.user));
            dispatch(setAuthenticated(true));
            dispatch(setShowLoginModal(false));
            // Social login is offered on BOTH the login and sign-up screens,
            // so dismiss the register modal too — otherwise completing Google
            // sign-in from the sign-up screen leaves the RegisterModal mounted
            // on top of the authenticated app.
            dispatch(setShowRegisterModal(false));
        } catch (error) {
            console.error('Google login error:', error);
            dispatch(setError('An error occurred. Please try again.'));
            dispatch(setIsLoading(false));
        }
    }
);

export const appleLogin = createAsyncThunk(
    'auth/appleLogin',
    async (_, { dispatch }) => {
        // Note: we deliberately do NOT set isLoading here. The Apple button has
        // its own applePending state in LoginModal — dispatching isLoading too
        // would spin BOTH the Apple button AND the form's "Logging in..." button
        // simultaneously, which looks broken.
        try {
            dispatch(setError(null));

            await new Promise(resolve => requestAnimationFrame(resolve));

            const response = await window.electron.auth.appleLogin();

            if (response.error) {
                const msg = errMsg(response.error);
                if (msg !== 'cancelled') {
                    dispatch(setError(msg));
                }
                return;
            }

            guardAccountSwitch(dispatch, response.user);
            if (!IS_WEB) {
                localStorage.setItem('auth:accessToken', response.accessToken);
                localStorage.setItem('auth:refreshToken', response.refreshToken);
            }
            dispatch(setAccessToken(response.accessToken));
            dispatch(setRefreshToken(response.refreshToken));
            dispatch(setUser(response.user));
            dispatch(setAuthenticated(true));
            dispatch(setShowLoginModal(false));
            // See googleLogin: also dismiss the sign-up screen's modal.
            dispatch(setShowRegisterModal(false));
        } catch (error) {
            console.error('Apple login error:', error);
            dispatch(setError('An error occurred. Please try again.'));
        }
    }
);

export const refreshAccessToken = createAsyncThunk(
    'auth/refresh',
    async (_, { dispatch, getState }) => {
        const { auth } = getState();
        // Web: cookie carries refresh token. Electron: must have it in state.
        if (!IS_WEB && !auth.refreshToken) {
            dispatch(clearAuth());
            dispatch(setShowLoginModal(true));
            return;
        }

        // Through the gate: shares an in-flight refresh with refreshAndRetry
        // and honours the auth service's 429 cooldown.
        const response = await requestRefresh(IS_WEB ? undefined : auth.refreshToken);

        if (!response.accessToken) {
            console.warn('Token refresh failed:', response.error);
            if (response.status === 401) {
                // Refresh token truly rejected — force login
                if (!IS_WEB) {
                    localStorage.removeItem('auth:accessToken');
                    localStorage.removeItem('auth:refreshToken');
                }
                dispatch(clearAuth());
                dispatch(setShowLoginModal(true));
            }
            // Network error — don't logout
            return;
        }

        if (!IS_WEB) {
            localStorage.setItem('auth:accessToken', response.accessToken);
        }
        dispatch(setAccessToken(response.accessToken));
        if (response.refreshToken) {
            // /refresh rotates and returns the new refresh token — mirror it
            // into Redux memory so subsequent calls see the live token. On
            // Electron it is also the copy the next launch restores from (main
            // persists its own too); on web the cookie is the durable copy.
            if (!IS_WEB) localStorage.setItem('auth:refreshToken', response.refreshToken);
            dispatch(setRefreshToken(response.refreshToken));
        }

        // Sync subscription_type and trial_started_at from the new JWT
        try {
            const payload = JSON.parse(atob(response.accessToken.split('.')[1]));
            const updates = {};
            if (payload.subscriptionType && payload.subscriptionType !== auth.user?.subscription_type) {
                updates.subscription_type = payload.subscriptionType;
            }
            if (payload.trialStartedAt && payload.trialStartedAt !== auth.user?.trial_started_at) {
                updates.trial_started_at = payload.trialStartedAt;
            }
            if (payload.trialEndsAt && payload.trialEndsAt !== auth.user?.trial_ends_at) {
                updates.trial_ends_at = payload.trialEndsAt;
            }
            if (Object.keys(updates).length > 0) {
                dispatch(setUser({ ...auth.user, ...updates }));
            }
        } catch (e) { /* ignore decode errors */ }
    }
);
